// ============================================================================
// Adam Platform — Active Rollback Banner (BUCKET I.8)
// ============================================================================
// Prominent banner at the top of the Releases cockpit when the active
// upgrade's status is `auto_rollback`. Displays reason, component scope,
// and the from/to version flip so the operator understands exactly which
// cohort tripped the rollback.
//
// P4: no optimistic copy. If `rollback` is missing we render nothing at
// all — callers should skip invoking the component instead of passing a
// stub.
// ============================================================================

export interface RollbackInfo {
  reason: string;
  component?: "gateway" | "hank" | "plugins_bundle" | "all";
  triggeredAt: string;
  fromTag: string;
  toTag: string;
}

interface ActiveRollbackBannerProps {
  rollback: RollbackInfo;
}

function formatComponentScope(c: RollbackInfo["component"]): string {
  if (!c || c === "all") return "all components";
  if (c === "plugins_bundle") return "plugins bundle";
  return c;
}

function formatTriggeredAt(iso: string): string {
  // Best-effort locale rendering. When the server sends a malformed value,
  // fall back to the raw string rather than "Invalid Date".
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

export function ActiveRollbackBanner({ rollback }: ActiveRollbackBannerProps) {
  return (
    <div
      data-testid="active-rollback-banner"
      role="alert"
      className="rounded-lg p-4 mb-6"
      style={{
        border: "1px solid var(--c-danger)",
        backgroundColor: "color-mix(in srgb, var(--c-danger) 10%, transparent)",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex items-center justify-center rounded-full"
          style={{
            width: 32,
            height: 32,
            backgroundColor: "color-mix(in srgb, var(--c-danger) 20%, transparent)",
            color: "var(--c-danger)",
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          <svg
            width={18}
            height={18}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 9v4m0 4h.01M4.93 19h14.14a2 2 0 0 0 1.73-3L13.73 4a2 2 0 0 0-3.46 0L3.2 16a2 2 0 0 0 1.73 3Z" />
          </svg>
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold" style={{ color: "var(--c-danger)" }}>
            Automatic rollback triggered
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--c-text-secondary)" }}>
            {rollback.reason}
          </div>
          <dl
            className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs"
            style={{ color: "var(--c-text-secondary)" }}
          >
            <div>
              <dt style={{ color: "var(--c-text-faint)" }}>Version flip</dt>
              <dd style={{ color: "var(--c-text)" }}>
                <code>{rollback.fromTag}</code> → <code>{rollback.toTag}</code>
              </dd>
            </div>
            <div>
              <dt style={{ color: "var(--c-text-faint)" }}>Component</dt>
              <dd style={{ color: "var(--c-text)" }}>{formatComponentScope(rollback.component)}</dd>
            </div>
            <div>
              <dt style={{ color: "var(--c-text-faint)" }}>Triggered</dt>
              <dd style={{ color: "var(--c-text)" }}>{formatTriggeredAt(rollback.triggeredAt)}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}

export default ActiveRollbackBanner;
